import { isPlayableChatPreview } from './chatPreviewQueue';
import { getPreviewTopic, previewTopicKey } from './previewTopic';

const textChip = (title, chipLabel = title) => ({
    title, chipLabel, chipImageUrl: '', chipImageShape: null,
});

const originPresentation = origin => {
    const name = typeof origin?.name === 'string' ? origin.name.trim() : '';
    const nameKey = previewTopicKey(name);
    const unknownName = !nameKey || previewTopicKey(origin?.id) === 'unknown'
        || ['unknown', 'unknown speaker', 'unidentified speaker', 'unknown person', 'unknown podcast', 'unknown source', 'unknown show'].includes(nameKey);
    switch (origin?.kind) {
        case 'saved': return textChip(name ? `${name} highlights` : 'Saved highlights', name || 'Saved');
        case 'search': return textChip('Search results', 'Search');
        case 'speaker':
        case 'podcast':
            if (unknownName) return textChip('Highlights');
            return {
                ...textChip(`${name} highlights`, name),
                // Capture the queue's source image at launch; a clip may feature another entity.
                chipImageUrl: typeof origin.imageURL === 'string' ? origin.imageURL.trim() : '',
                chipImageShape: origin.kind === 'speaker' ? 'circle' : 'square',
            };
        case 'episode': return textChip('Episode highlights', 'Episode');
        case 'shared': return textChip('Shared highlight', 'Shared');
        default: return textChip('Highlights');
    }
};

const playbackSegment = (context, contextTopic, queue, previewId) => {
    if (previewTopicKey(contextTopic)) return 'topic';
    if (context?.kind === 'otherTopics') return 'otherTopics';
    // Older in-memory queues used this kind even when no topic was available.
    if (context?.kind === 'otherHighlights') {
        return previewTopicKey(queue?.playbackTopic) || previewTopicKey(queue?.relatedTopicId) ? 'otherTopics' : 'moreHighlights';
    }
    if (context?.kind === 'moreHighlights') return 'moreHighlights';
    if (context?.kind === 'answer') return 'answer';
    if (context?.kind === 'origin') return 'origin';
    if (queue?.playbackSource === 'chat') {
        const responseIds = queue.responsePreviewIds;
        return !Array.isArray(responseIds) || responseIds.includes(previewId) ? 'answer' : 'moreHighlights';
    }
    return 'origin';
};

// Queue context comes from playback metadata, never from a clip's topic tags.
export const getPreviewQueuePresentation = (queue, currentPreviewId) => {
    const previews = Array.isArray(queue?.previewsInfo) ? queue.previewsInfo : [];
    const contexts = queue?.playbackContextById || {};
    const seen = new Set();
    const rows = previews.flatMap((preview, index) => {
        if (!isPlayableChatPreview(preview) || seen.has(preview.id)) return [];
        seen.add(preview.id);
        const context = contexts[preview.id];
        const contextTopic = context?.kind === 'topic' && typeof context.topic === 'string' ? context.topic.trim() : '';
        const segment = playbackSegment(context, contextTopic, queue, preview.id);
        return [{
            preview,
            index,
            isCurrent: preview.id === currentPreviewId,
            segment,
            topic: segment === 'topic' ? contextTopic : getPreviewTopic(preview, { uppercase: false }),
        }];
    });

    const current = rows.find(row => row.isCurrent) || null;
    const currentIndex = current?.index ?? -1;
    const earlier = current ? rows.filter(row => row.index < currentIndex) : [];
    const upcoming = current ? rows.filter(row => row.index > currentIndex) : [];
    const activeTopic = current?.segment === 'topic' ? current.topic : '';
    const segmentPresentation = row => {
        switch (row?.segment) {
            case 'topic': return textChip(row.topic);
            case 'answer': return textChip('From chat');
            case 'otherTopics': return textChip('Random highlights', 'Random');
            case 'moreHighlights': return textChip('More highlights');
            default: return originPresentation(queue?.playbackOrigin);
        }
    };
    const presentation = segmentPresentation(current);

    // Keep contiguous groups in playback order, including every continuation clip.
    const upcomingGroups = [];
    upcoming.forEach(row => {
        const previousGroup = upcomingGroups[upcomingGroups.length - 1];
        if (previousGroup?.segment === row.segment) {
            previousGroup.rows.push(row);
        } else {
            upcomingGroups.push({
                id: `upcoming-${row.index}`,
                segment: row.segment,
                label: row.segment !== current?.segment && ['otherTopics', 'moreHighlights'].includes(row.segment)
                    ? segmentPresentation(row).title : 'Up next',
                rows: [row],
            });
        }
    });
    return { ...presentation, activeTopic, currentIndex, rows, earlier, current, upcoming, upcomingGroups };
};
